import { useEffect, useState } from 'react'
import { request } from '@/lib/request'
import { useConfigStore } from '@/store/config'

export type CommissionScope = 'user' | 'oem'

export interface CommissionSwitches {
  regional_enabled: boolean
  promoter_enabled: boolean
  service_enabled: boolean
}

export interface CommissionVisibility {
  loading: boolean
  switches: CommissionSwitches
  has_regional_record: boolean
  has_promoter_record: boolean
  has_service_record: boolean
  has_cash_balance: boolean
  has_operator_balance: boolean
}

/**
 * 纯同步解析 publicConfig 中的三个开关态（首帧可用，不触发异步请求）。
 * 用于全局组件（如 Header 现金徽章）避免闪烁。
 */
export function parseCommissionSwitches(config: any): CommissionSwitches {
  const parse = (v: any) => v !== false && v !== '0' && v !== 0 && v !== 'false'
  return {
    regional_enabled: parse(config?.regional_commission_enabled),
    promoter_enabled: parse(config?.promoter_commission_enabled),
    service_enabled: parse(config?.service_commission_enabled),
  }
}

const NO_SWITCHES: CommissionSwitches = {
  regional_enabled: false,
  promoter_enabled: false,
  service_enabled: false,
}

/**
 * 分销三开关可见性 hook。
 *
 * 设计要点（方案书 8.2 硬要求·无闪烁）：
 *  - 开关态始终以 publicConfig 为准（首帧即可得，不闪烁）；
 *  - config 尚未就绪时视为「未确定」，loading=true，OFF 类面板先占位不渲染，绝不「先全显再消失」；
 *  - 「有无记录 / 余额」来自异步接口，loading 期间该部分未知。
 *
 * 8.1 显隐公式（逐开关）：
 *  - 用户端（个人中心）身份/分佣面板：开关 ON → 显示；OFF → 整块彻底隐藏（不保留只读）。
 *    对应面板：运营商身份(regional) / 服务商身份(service) / 分佣与结算(promoter)。
 *  - 代理后台（oem_admin）记录类入口：开关 ON → 显示；OFF + 有记录 → 只读保留；OFF + 无记录 → 隐藏。
 *  - 操作类入口（生成链接 / 设置 / 审核）：始终仅在开关 ON 时显示。
 */
export function useCommissionVisibility(scope: CommissionScope): CommissionVisibility {
  const config = useConfigStore((s) => s.config)
  const [records, setRecords] = useState({
    has_regional_record: false,
    has_promoter_record: false,
    has_service_record: false,
    has_cash_balance: false,
    has_operator_balance: false,
  })
  const [apiLoading, setApiLoading] = useState(true)

  // 开关态始终以 publicConfig 为准；config 未就绪时视为未确定（全部 false + loading）
  const switches: CommissionSwitches = config ? parseCommissionSwitches(config) : NO_SWITCHES

  useEffect(() => {
    let cancelled = false
    const path =
      scope === 'user'
        ? '/api/user/distribution/visibility'
        : '/api/oem_admin/distribution/visibility'
    request<any>(path, { method: 'GET' })
      .then((data) => {
        if (cancelled || !data) return
        setRecords({
          has_regional_record: !!data.has_regional_record,
          has_promoter_record: !!data.has_promoter_record,
          has_service_record: !!data.has_service_record,
          has_cash_balance: !!data.has_cash_balance,
          has_operator_balance: !!data.has_operator_balance,
        })
      })
      .catch(() => {
        // 接口失败时不阻塞：按「无记录」处理（OFF 类面板保持隐藏，防御性）
      })
      .finally(() => {
        if (!cancelled) setApiLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope])

  return {
    loading: !config || apiLoading,
    switches,
    ...records,
  }
}
